/**
 * fileHandleStore.js
 *
 * Persistent storage for File System Access API handles.
 *
 * FileSystemFileHandle / FileSystemDirectoryHandle objects are
 * structured-cloneable, so IndexedDB can serialise them directly — no extra
 * plumbing. That means a handle picked in one session survives page reloads,
 * and pidly can re-open "local" project files without asking the user to
 * re-pick anything (apart from the one-click permission re-prompt that the
 * browser enforces for security).
 *
 * Used by the File > Open Files / Open Folder flow to store handles keyed
 * by an internal file id, and by the PDF loader to pull bytes straight from
 * disk without a backend round-trip.
 */

const DB_NAME = 'pidly_fsa';
const DB_VERSION = 1;
const STORE = 'handles';

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(mode) {
  const db = await openDB();
  return db.transaction(STORE, mode).objectStore(STORE);
}

export async function saveHandle(key, handle) {
  const store = await tx('readwrite');
  return new Promise((resolve, reject) => {
    const req = store.put(handle, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function getHandle(key) {
  const store = await tx('readonly');
  return new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function deleteHandle(key) {
  const store = await tx('readwrite');
  return new Promise((resolve, reject) => {
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export async function listKeys() {
  const store = await tx('readonly');
  return new Promise((resolve, reject) => {
    const req = store.getAllKeys();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

// Check / request read permission for a handle.
//
// Browser rule: after a reload, a handle defaults to 'prompt' and the user
// has to re-confirm inside a user gesture. We query first, then request if
// needed. Pass { request: false } to do a dry-run check without triggering
// a prompt (useful before the user has clicked anything).
export async function verifyPermission(handle, { request = true } = {}) {
  if (!handle || typeof handle.queryPermission !== 'function') return false;
  const opts = { mode: 'read' };
  try {
    const state = await handle.queryPermission(opts);
    if (state === 'granted') return true;
    if (!request) return false;
    const next = await handle.requestPermission(opts);
    return next === 'granted';
  } catch {
    return false;
  }
}

export function isFileSystemAccessSupported() {
  return typeof window !== 'undefined'
    && typeof window.showOpenFilePicker === 'function'
    && typeof window.showDirectoryPicker === 'function';
}
